// Modules
import { FormEvent, useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import pick from "lodash.pick";
// Material-UI
import { useTheme } from "@mui/material/styles";
import {
    Autocomplete,
    Button,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
    Switch
} from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { Group } from "@mui/icons-material";
// Actions
import { getPatientsSWR } from "@/actions/shared/patientActions";
// Components
import InitializeLocation from "../../../AppLayout/InitializeLocation";
import AlertDialog from "../../extended/AlertDialog";
// Helpers
import { BOOK_GROUPED_SESSION, FREQ_EVERYXWEEKS, FREQ_NONE, FREQ_WEEKLY } from "../../../../helpers/constants";
import { RECURRENCE } from "./helpers/recurrence";
// Types
import { Therapist } from "@/lib/types/users";
import { Event, SessionEvent, Guest } from "./types/event";
import { RRule } from "./types/rules";
import { AlertDialogAction } from "../../extended/AlertDialog/types";
// Styles
import { gridSpacing } from "../../../../styles/constants";
// Assets
import DateRangeIcon from "@mui/icons-material/DateRange";
import { User } from "@/lib/types/types";

interface FormValues {
    type: string;
    title: string;
    description: string;
    color: string;
    start: Date | undefined;
    end: Date | undefined;
    guests: Array<{
        name: string;
        email: string;
        childName?: string;
    }>;
    allDay: boolean;
    rrule: RRule;
    recurrence: string;
    exdate?: string[];
    exrule?: any;
    duration: string;
}

const RECURRENCE_OPTIONS = pick(RECURRENCE, [FREQ_NONE, FREQ_WEEKLY, FREQ_EVERYXWEEKS]);
const MAX_ALLOWED_GUESTS = 2;

interface FormTypeProps {
    showRecurrence: boolean;
    patient: Guest[];
    flags: { canBookGroupedSession: boolean };
    assignedTherapistId?: string;
    availableSessionsUpdate: () => void;
}

interface BookEventFormTypes {
    locale: string;
    updateEvent?: Event;
    therapist: Therapist;
    creationType: string;
    onCancel: () => void;
    handleCreate: (args: { id: string; data: any; successCb: () => void }) => void;
    bookEventFormProps: FormTypeProps;
    hasPermission: (permission: string) => boolean;
}

export interface AlertDialogState {
    opened: boolean;
    options?: AlertDialogAction;
    dates?: string[];
}

const defaultAlertDialogAction: AlertDialogAction = {
    action: () => {},
    label: "",
    title: "",
    description: ""
};

const defaultAlertDialogState: AlertDialogState = {
    opened: false,
    options: defaultAlertDialogAction
};

const getMinUntilDate = (date: Date) => {
    let minUntilDate = new Date(date);
    minUntilDate.setDate(minUntilDate.getDate() + 1);
    return minUntilDate;
};

const getInitialValues = ({
    creationType,
    theme,
    updateEvent,
    hasProps,
    bookEventFormProps
}: {
    creationType: string;
    theme: any;
    updateEvent?: Event;
    hasProps: boolean;
    bookEventFormProps: FormTypeProps;
}) => {
    const { patient: guest } = bookEventFormProps || {};
    const defaultRrule: RRule = { freq: FREQ_NONE };
    const rrule = updateEvent?.rrule || defaultRrule;
    if (rrule.until) rrule.until = new Date(rrule.until);

    const startDate = updateEvent?.start ? new Date(updateEvent.start) : undefined;
    const endDate = new Date(startDate!);
    endDate.setHours(endDate.getHours() + 1);

    return {
        type: creationType,
        title: updateEvent?.title || "",
        description: updateEvent?.description || "",
        color: theme.palette.primary.main,
        start: startDate,
        end: endDate,
        guests:
            (updateEvent?.guests?.length ?? 0) > 0
                ? updateEvent?.guests ?? []
                : hasProps
                ? guest?.map(({ name, email }: Guest) => ({ name, email }))
                : [],
        allDay: updateEvent?.allDay || false,
        rrule,
        recurrence: defaultRrule.freq,
        exdate: updateEvent?.exdate,
        exrule: updateEvent?.exrule,
        duration: "01:00"
    };
};

const buildTherapyTitle = ({
    intl,
    therapist,
    guest,
    isGroupedSession,
    isAssignedTherapist
}: {
    intl: IntlShape;
    therapist: Therapist;
    guest?: Guest | FormValues["guests"][0];
    isGroupedSession: boolean;
    isAssignedTherapist: boolean;
}) => {
    const therapyLabel = intl.formatMessage({ id: "therapy" });
    const guestName = guest && "childName" in guest ? guest.childName : guest?.name;
    const sessionType = isGroupedSession || isAssignedTherapist ? "" : guestName;
    return `${therapyLabel} ${therapist?.name}${sessionType ? ` - ${sessionType}` : ""}`;
};

const getPatientsAndTherapistStatus = (bookEventFormProps: FormTypeProps, therapist: Therapist) => {
    const hasProps = Object.keys(bookEventFormProps).length > 0;
    const { patient, assignedTherapistId, flags } = bookEventFormProps || {};

    const guest: Guest[] | User[] = hasProps
        ? patient.map(({ name, email }: Guest) => ({ name, email }))
        : getPatientsSWR({ displaySpinner: false });
    const isAssignedTherapist = hasProps && therapist?._id === assignedTherapistId;

    return { hasProps, guest, isAssignedTherapist, canBookGroupedSession: flags?.canBookGroupedSession };
};

const BookEventForm = ({
    locale,
    updateEvent,
    therapist,
    creationType,
    onCancel,
    handleCreate,
    bookEventFormProps,
    hasPermission
}: BookEventFormTypes) => {
    const theme = useTheme();
    const intl = useIntl();

    const [openAlertDialog, setAlertDialogOpen] = useState<AlertDialogState>(defaultAlertDialogState);
    const [isGroupedSession, setIsGroupedSession] = useState(updateEvent?.isGroupedSession || false);

    const { guests = [] } = updateEvent || {};
    const initialMaxParticipants = guests.length || (isGroupedSession ? MAX_ALLOWED_GUESTS : 0);
    const [maxParticipants, setMaxParticipants] = useState(initialMaxParticipants);

    /*
     * bookEventFormProps is received from the patient substitute view
     * and is valid only for patients coming from that context to enable
     * additional functionality for those patients.
     */
    const { hasProps, guest, isAssignedTherapist, canBookGroupedSession } = getPatientsAndTherapistStatus(
        bookEventFormProps,
        therapist
    );

    const handleCloseAlertDialog = () => {
        setAlertDialogOpen(prevState => ({ ...prevState, opened: false }));
        setTimeout(() => {
            setAlertDialogOpen(prevState => ({
                ...prevState,
                options: defaultAlertDialogAction,
                opened: prevState?.opened ?? false
            }));
        }, 500);
    };

    const handleIsGroupedSessionCheck = (event: { target: { checked: any } }) => {
        const isChecked = event.target.checked;
        setIsGroupedSession(isChecked);
        setFieldValue("recurrence", FREQ_NONE);

        if (isGroupedSession) setFieldValue("guests", []);

        // Set max participants state to 0, to allow user to select
        setMaxParticipants(0);
    };

    const formik = useFormik<FormValues>({
        initialValues: getInitialValues({
            creationType,
            theme,
            updateEvent,
            hasProps,
            bookEventFormProps
        }),
        validationSchema: Yup.object().shape({
            title: Yup.string().max(255),
            start: Yup.date().required(intl.formatMessage({ id: "error-required-field" })),
            color: Yup.string().max(255),
            guests: Yup.array().min(1, intl.formatMessage({ id: "error-required-field" }))
        }),
        onSubmit: async (values, { setErrors }) => {
            const { start, end, recurrence, rrule, exrule, guests, description, color, allDay, type } = values;
            const startDate = start;
            const endDate = end;

            // Handle Recurrence Validation
            if (recurrence !== FREQ_NONE) {
                const { until } = rrule || {};
                if (until && startDate && startDate >= until) {
                    setErrors({ rrule: { until: intl.formatMessage({ id: "error-invalid-date-min-start-date" }) } });
                    return;
                }

                if (formik.initialValues.rrule?.until && (until ?? new Date()) > formik.initialValues.rrule.until) {
                    setErrors({
                        rrule: { until: intl.formatMessage({ id: "error-invalid-date-max-free-slot-until-date" }) }
                    });
                    return;
                }
            }

            // Build session object
            const session: SessionEvent = {
                type,
                status: "confirmed",
                free_slot: updateEvent?.id,
                title: buildTherapyTitle({
                    intl,
                    therapist,
                    guest: guests?.[0],
                    isGroupedSession,
                    isAssignedTherapist
                }),
                description: description?.length ? description : undefined,
                color,
                start: startDate ? startDate?.toISOString() : new Date(),
                end: endDate ? endDate?.toISOString() : new Date(),
                guests: guests?.map(guest => ({ name: guest.name, email: guest.email })),
                allDay,
                ...(isGroupedSession || (isAssignedTherapist && canBookGroupedSession)
                    ? { isGroupedSession: true }
                    : {}),
                recurrence,
                ...(recurrence !== FREQ_NONE && {
                    rrule: {
                        ...rrule,
                        ...(RECURRENCE_OPTIONS[recurrence]?.props || {}),
                        interval: RECURRENCE[recurrence]?.hasCustomInterval ? rrule?.interval : 1,
                        dtstart: startDate?.toISOString(),
                        until: rrule?.until?.toISOString() || formik.initialValues.rrule?.until?.toISOString()
                    }
                }),
                ...(recurrence !== FREQ_NONE && { exrule }),
                ...(recurrence !== FREQ_NONE &&
                    (updateEvent?.exdate?.length ?? 0) > 0 && {
                        exdate: startDate
                            ? updateEvent?.exdate?.filter(date => new Date(date) >= startDate)
                            : updateEvent?.exdate
                    }),
                user: {
                    _id: therapist?._id,
                    name: therapist?.name,
                    email: therapist?.email
                }
            };

            if (hasProps) {
                session.isSubstituted = true;
                session.status = "pending";
                session.isSubstituteEvent = true;
                session.user.meetingLink = therapist?.meeting_link;
            }

            const findConflictingDates =
                updateEvent?.bookedDates?.filter(
                    date =>
                        (!startDate || new Date(date) > new Date(startDate)) &&
                        (!rrule?.until || new Date(date) <= new Date(rrule.until))
                ) ?? [];

            // Determine if alert dialog should be shown when booking recurring events and alrady booked dates are avaible
            const shouldShowAlert =
                session.recurrence !== FREQ_NONE &&
                (updateEvent?.bookedDates?.length ?? 0) > 0 &&
                (findConflictingDates?.length ?? 0) > 0;

            if (shouldShowAlert) {
                setAlertDialogOpen({
                    opened: true,
                    options: {
                        action: () => {
                            handleCreate({
                                id: therapist._id,
                                data: session,
                                successCb: () => {}
                            });
                        },

                        label: "create",
                        description: "dates-conflict-warning"
                    },
                    // only show dates in the future
                    dates: findConflictingDates
                });
            } else {
                handleCreate({
                    id: therapist._id,
                    data: session,
                    successCb: () => {}
                });
            }
        }
    });

    useEffect(() => {
        return () => {
            formik?.resetForm();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { initialValues, values, errors, touched, getFieldProps, setFieldValue } = formik;

    const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        formik.handleSubmit(e);
    };

    return (
        <InitializeLocation adapter="date" locale={locale}>
            <form noValidate onSubmit={handleSubmit}>
                <DialogTitle>
                    <FormattedMessage id={"book-free-slot"} />
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <TextField
                                disabled
                                fullWidth
                                label={<FormattedMessage id="title" />}
                                value={buildTherapyTitle({
                                    intl,
                                    therapist,
                                    guest: hasProps ? guest[0] : values.guests?.[0],
                                    isGroupedSession,
                                    isAssignedTherapist
                                })}
                            />
                        </Grid>
                        <Grid item xs={6} sx={{ minWidth: "220px" }}>
                            <MobileDateTimePicker
                                disabled
                                label={<FormattedMessage id={"start-date"} />}
                                format="dd/MM/yyyy HH:mm"
                                onChange={() => {}}
                                // slotProps={(params: TextFieldProps) => (
                                //     <TextField
                                //         {...params}
                                //         fullWidth
                                //         InputProps={{
                                //             endAdornment: (
                                //                 <InputAdornment position="end">
                                //                     <DateRangeIcon />
                                //                 </InputAdornment>
                                //             )
                                //         }}
                                //     />
                                // )}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        InputProps: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <DateRangeIcon />
                                                </InputAdornment>
                                            )
                                        }
                                    }
                                }}
                                value={values.start}
                            />
                        </Grid>
                        <Grid item alignSelf="center">
                            <Typography variant="body1">
                                <FormattedMessage id="1hour-event" />
                            </Typography>
                        </Grid>
                        {hasPermission(BOOK_GROUPED_SESSION) && (
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={12} sx={{ minWidth: "min-content" }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={isGroupedSession}
                                                    onChange={handleIsGroupedSessionCheck}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Stack
                                                    spacing={1}
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    sx={{ width: "100%" }}
                                                >
                                                    <Group color="primary" />
                                                    <Stack spacing={0} sx={{ width: "100%" }}>
                                                        <Typography variant="subtitle1" color="primary">
                                                            <FormattedMessage id="is-grouped-session" />
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}

                        {updateEvent?.recurrence !== FREQ_NONE && !hasProps && !isGroupedSession && (
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1">
                                            <FormattedMessage id="recurrence" />*
                                        </Typography>
                                    </Grid>
                                    <Grid container>
                                        <Grid item xs={6} sx={{ minWidth: "min-content" }}>
                                            <FormControl>
                                                <RadioGroup
                                                    row
                                                    aria-label="recurrence"
                                                    {...getFieldProps("recurrence")}
                                                    onChange={e => setFieldValue("recurrence", e.target.value)}
                                                    name="recurrence-radio-buttons-group"
                                                    sx={{ "& .MuiFormControlLabel-root": { mr: 0 } }}
                                                >
                                                    {Object.entries(RECURRENCE_OPTIONS).map(([item, value]) => (
                                                        <Grid item xs={12} key={item}>
                                                            <FormControlLabel
                                                                value={item}
                                                                control={<Radio />}
                                                                label={
                                                                    value?.getLabel ? (
                                                                        value.getLabel(formik)
                                                                    ) : (
                                                                        <FormattedMessage id={item} />
                                                                    )
                                                                }
                                                                sx={{ minWidth: "max-content" }}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6} alignSelf="flex-end" sx={{ mt: "32px", minWidth: "200px" }}>
                                            <MobileDatePicker
                                                disabled={values.recurrence === FREQ_NONE}
                                                label={<FormattedMessage id={"repeats-until"} />}
                                                value={values.rrule?.until || null}
                                                format="dd/MM/yyyy"
                                                minDate={values.start ? getMinUntilDate(values.start) : new Date()}
                                                maxDate={initialValues?.rrule?.until}
                                                defaultCalendarMonth={
                                                    values.start ? getMinUntilDate(values.start) : new Date()
                                                }
                                                onChange={date => {
                                                    // To include that day
                                                    date && date.setHours(23, 59, 59, 999);
                                                    setFieldValue("rrule.until", date);
                                                }}
                                                // okText={<FormattedMessage id="ok" />}
                                                // cancelText={<FormattedMessage id="cancel" />}
                                                // clearText={<FormattedMessage id="clear" />}
                                                // textField={(params: TextFieldProps) => (
                                                //     <TextField
                                                //         {...params}
                                                //         fullWidth
                                                //         InputProps={{
                                                //             endAdornment: (
                                                //                 <InputAdornment position="end">
                                                //                     <DateRangeIcon />
                                                //                 </InputAdornment>
                                                //             )
                                                //         }}
                                                //         error={Boolean(errors.rrule?.until)}
                                                //         helperText={errors.rrule?.until}
                                                //     />
                                                // )}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        InputProps: {
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <DateRangeIcon />
                                                                </InputAdornment>
                                                            )
                                                        },
                                                        error: Boolean(errors.rrule?.until),
                                                        helperText: errors.rrule?.until
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                disabled
                                fullWidth
                                label={<FormattedMessage id="therapist" />}
                                value={`${therapist.name} - ${therapist.email}`}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                id="guests"
                                multiple
                                disabled={hasProps}
                                limitTags={2}
                                filterOptions={(options, state) => {
                                    const maxGuests = isGroupedSession ? MAX_ALLOWED_GUESTS : 1;
                                    const searchValue = state.inputValue.toLowerCase();

                                    return options.filter(option => {
                                        const { name = "", email = "" } = option || {};

                                        // Check if the search input matches either the name or the email
                                        const matchesSearch = [name, email].some(field =>
                                            (field ?? "").toLowerCase().includes(searchValue)
                                        );

                                        // Ensure that guests can still be added
                                        const canAddMoreGuests = maxParticipants < maxGuests;
                                        return matchesSearch && canAddMoreGuests;
                                    });
                                }}
                                filterSelectedOptions
                                getOptionLabel={option =>
                                    `${option.childName} (${intl.formatMessage({ id: "parent" })}: ${option.name}) - ${
                                        option.email
                                    }`
                                }
                                isOptionEqualToValue={(option, value) => option?.email === value?.email}
                                noOptionsText={<FormattedMessage id="no-options" />}
                                options={[...(guest || [])].map(option => ({
                                    name: option.name,
                                    email: option.email,
                                    childName: option.child?.name
                                }))}
                                onChange={(_, value) => {
                                    const maxGuests = isGroupedSession ? MAX_ALLOWED_GUESTS : 1;

                                    if (value && value.length <= maxGuests) {
                                        setMaxParticipants(value.length);
                                        setFieldValue("guests", value);
                                    }
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        id={"guests"}
                                        label={<FormattedMessage id="guests" />}
                                        name={"guests"}
                                        error={touched.guests && Boolean(errors.guests)}
                                        helperText={
                                            touched.guests && typeof errors.guests === "string"
                                                ? errors.guests
                                                : undefined
                                        }
                                    />
                                )}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => {
                                        // in substitute view, show only parent name and email
                                        const label = bookEventFormProps.patient
                                            ? `${option.name} - ${option.email})`
                                            : `${option.childName} (${intl.formatMessage({ id: "parent" })}: ${
                                                  option.name
                                              }) - ${option.email}`;

                                        return (
                                            // eslint-disable-next-line react/jsx-key
                                            <Chip label={label} {...getTagProps({ index })} />
                                        );
                                    })
                                }
                                value={values?.guests}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <Grid container justifyContent="flex-end" alignItems="center">
                        <Grid item>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button type="button" variant="outlined" onClick={onCancel}>
                                    <FormattedMessage id="cancel" />
                                </Button>

                                <Button type="submit" variant="contained" data-testid="create-booked-event">
                                    <FormattedMessage id="create" />
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogActions>
            </form>
            <AlertDialog
                open={openAlertDialog?.opened || false}
                actions={{
                    primary: openAlertDialog?.options || defaultAlertDialogAction,
                    secondary: {
                        action: handleCloseAlertDialog,
                        label: "cancel"
                    }
                }}
                description={
                    openAlertDialog?.options?.description
                        ? intl.formatMessage({ id: openAlertDialog.options.description })
                        : undefined
                }
                dates={openAlertDialog?.dates ?? []}
                handleClose={handleCloseAlertDialog}
            />
        </InitializeLocation>
    );
};

export default BookEventForm;
